import React, { forwardRef, ReactNode } from 'react'
import { Paper, Typography, Box } from '@mui/material'
import { Chart } from 'react-chartjs-2'
import { ChartData, ChartOptions, Plugin } from 'chart.js'
import styled from 'styled-components'
import { ChartEmptyState } from '../../ChartEmptyState'
import LoadingIndicator from '../../../../shared/UI/LoadingIndicator'

const LoadingIndicatorContainer = styled.div`
  padding-top: 70px;
  padding-bottom: 70px;
`

interface ChartWrapperProps {
  title?: string
  error?: Error | null
  isLoading?: boolean
  hasData?: boolean
  emptyStateMessage?: string
  chartType: 'line' | 'bar' | 'doughnut' | 'pie' | 'scatter' | 'bubble' | 'polarArea' | 'radar'
  chartData?: ChartData<any>
  chartOptions?: ChartOptions<any>
  chartPlugins?: Plugin[]
  height?: number | string
  customEmptyState?: ReactNode
  headerAction?: ReactNode
}

/**
 * Common wrapper component for all charts with error handling, loading, and empty states
 */
const ChartWrapper = forwardRef<any, ChartWrapperProps>(({
  title,
  error,
  isLoading = false,
  hasData = true,
  emptyStateMessage,
  chartType,
  chartData,
  chartOptions,
  chartPlugins,
  height = 500,
  customEmptyState,
  headerAction
}, ref) => {
  // Loading state
  if (isLoading) {
    return (
      <Paper elevation={2} sx={{ p: 3, minHeight: height }}>
        <LoadingIndicatorContainer>
          <LoadingIndicator show={isLoading} />
        </LoadingIndicatorContainer>
      </Paper>
    )
  }

  // Error state
  if (error) {
    return (
      <Paper 
        elevation={2} 
        sx={{ 
          p: 3, 
          minHeight: height, 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center' 
        }}
      >
        <Typography variant="h6" color="error">
          Error loading chart:
          <br />
          {error.message}
        </Typography>
      </Paper>
    )
  }

  // Empty state
  if (!hasData || !chartData) {
    if (customEmptyState) {
      return <>{customEmptyState}</>
    }
    
    return (
      <ChartEmptyState 
        title={title} 
        message={emptyStateMessage}
      />
    )
  }

  // Chart render
  return (
    <Paper elevation={2} sx={{ p: 3, height: typeof height === 'number' ? height : undefined }}>
      {(title || headerAction) && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2
          }}
        >
          {title && (
            <Typography
              variant="h5"
              component="h2"
              sx={{
                fontWeight: 'bold',
                color: 'black',
                fontSize: '1rem',
                flex: 1,
                textAlign: 'center'
              }}
            >
              {title}
            </Typography>
          )}
          {headerAction && (
            <Box sx={{ ml: 'auto' }}>
              {headerAction}
            </Box>
          )}
        </Box>
      )}
      <Box sx={{ position: 'relative', height: typeof height === 'number' ? `${height - 72}px` : '428px', width: '100%' }}>
        <Chart
          ref={ref}
          type={chartType}
          options={chartOptions}
          data={chartData}
          {...(chartPlugins && { plugins: chartPlugins })}
        />
      </Box>
    </Paper>
  )
})

ChartWrapper.displayName = 'ChartWrapper'

export { ChartWrapper }
export type { ChartWrapperProps }